import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { CmsEntities, bindToContent } from 'client/data/models/cms';
import { TrackingHandler } from 'client/tracking/handler';
import { EditorialMap } from 'client/tracking/maps/editorial';
import { YoutubeVideoModalAsync } from 'site-modules/shared/components/youtube-video-modal/youtube-video-modal-async';
import { FACEBOOK_LANDING_PATH } from 'site-modules/brand-landing/constants/facebook-landing';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { VideoPreview } from 'site-modules/shared/components/video-preview/video-preview';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { LogoSection } from 'site-modules/shared/components/global-navigation-v2/logo-section';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { Carousel } from 'site-modules/shared/components/carousel/carousel';
import { RenderWhenViewable } from 'site-modules/shared/components/render-when-viewable/render-when-viewable';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { leftQuotesSymbol, leftQuotesUse } from 'client/images/inline-svgs/ads/brand-landing';

import './facebook-landing.scss';

export const getHeroImage = (heroImage, isMobile) => {
  const image = heroImage.metadata('image').value();
  const imageMobile = heroImage.metadata('imageMobile').value();
  return isMobile && imageMobile ? imageMobile : image;
};

const creativeIds = {
  heroHeader: 'edm-entry-hero-header',
  topNav: 'edm-entry-top-nav',
  testimonials: 'edm-entry-testimonials',
  whatWeDo: 'edm-entry-what-we-do',
  faq: 'edm-entry-faq',
  carShopping: 'edm-entry-car-shopping',
  getStarted: 'edm-entry-get-started',
};

const heroBg = heroImage => (
  <div
    className="pos-a w-100 hero-bg"
    style={{
      height: 'inherit',
      backgroundImage: `url(${getStaticImageUrl(heroImage)})`,
      backgroundSize: 'cover',
      backgroundPosition: '50% 16%',
    }}
    aria-hidden
  />
);

export class FacebookLandingUI extends Component {
  static propTypes = {
    content: CmsEntities.Content,
    isMobile: PropTypes.bool,
    isSmall: PropTypes.bool,
    breakpoint: PropTypes.string,
  };

  static defaultProps = {
    content: null,
    isMobile: false,
    isSmall: false,
    breakpoint: 'xl',
  };

  constructor(props) {
    super(props);

    this.state = {
      hasVideoAdRendered: true,
      isVideoModalOpen: false,
      videoModalData: {},
    };
  }

  componentDidMount() {
    TrackingHandler.useMap({ ...EditorialMap });
  }

  onToggleVideoModal = videoModalData => {
    if (this.state.isVideoModalOpen) {
      return this.setState({
        isVideoModalOpen: false,
      });
    }

    return this.setState({
      isVideoModalOpen: true,
      videoModalData,
    });
  };

  handleClickThruTracker = (creativeId, value) => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
      event_data: {
        action_name: TrackingConstant.ACTION_VIEW_CONTENT,
        subaction_name: TrackingConstant.ACTION_VIEW_CONTENT,
        action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        creative_id: creativeId,
        value,
      },
    });
  };

  renderHeroVideo = video => {
    const videoId = video.metadata('youtube-videoid').value();
    const videoThumbnailUrl = video.metadata('thumbnailURL').value();
    return (
      <Col
        xs={12}
        md={6}
        tag="figure"
        className="highlights-video-container mx-auto px-0 ps-md-0 pe-md-1 pe-lg-2_5 mb-0"
      >
        <VideoPreview
          videoId={videoId}
          videoTitle={video.metadata('videoTitle').value()}
          videoAspectRatio={'16:9'}
          videoThumbnailUrl={videoThumbnailUrl ? getStaticImageUrl(videoThumbnailUrl) : null}
          openVideoModal={this.onToggleVideoModal}
          creativeId={'Edmunds-Car-Shopping-Made-Easy'}
        />
      </Col>
    );
  };

  renderHeroHighlights = (title, subtitle, video, hasVideo) => (
    <Row tag="section" className="text-center text-white px-1 px-md-0" key={title}>
      <Col xs={12} className="my-1 my-md-2 ">
        <LogoSection useLogo226 />
      </Col>
      <Col
        xs={12}
        md={hasVideo ? 6 : null}
        tag="article"
        className="d-flex flex-column justify-content-center text-shadow-contrast"
      >
        <h1 className="text-white mb-1 mt-1 mx-auto text-md-start">{title}</h1>
        <h2 className="mx-auto text-md-start size-20 mb-2 mb-md-0">{subtitle}</h2>
      </Col>
      {hasVideo && this.renderHeroVideo(video)}
    </Row>
  );

  renderCategoryTopNav = (entries, creativeId, isRowLt3) =>
    entries.map((entry, index) => (
      <a
        href={entry.metadata('tileURL').value()}
        key={`entry-${entry.metadata('categoryTitle').value()}`}
        className={classnames('pos-r align-items-center px-lg-1 mx-lg-1 d-flex bg-c-primary text-color-primary', {
          'mx-0_25': index !== 0 && index !== entries.length - 1 && !isRowLt3, // middle cards and more
          'ms-0_5 me-0_25': index === 0 && !isRowLt3,
          'ms-0_25 me-0_5': index === entries.length - 1 && !isRowLt3,
          'px-1 px-md-0_5 mx-1 flex-md-column mb-1 mb-md-0': isRowLt3,
          'px-0_5 flex-column': !isRowLt3,
        })}
        onClick={this.handleClickThruTracker.bind(
          this,
          creativeId,
          entry.metadata('categoryTitle').value().toLowerCase()
        )}
      >
        <figure
          className={classnames('category-icon mt-lg-1_5 d-flex justify-content-center align-items-center', {
            'mx-auto mb-0_75 mb-lg-1 mt-0_75': !isRowLt3,
            'mx-md-auto me-1 mb-0 mt-md-0_75': isRowLt3,
          })}
          style={{
            width: '64px',
            height: '64px',
          }}
        >
          <img
            src={getStaticImageUrl(entry.metadata('categoryIcon').value())}
            alt={`${entry.metadata('categoryTitle').value()} logo`}
            width={24}
            height={25}
          />
        </figure>
        <div
          className={classnames('title-info d-flex flex-column', {
            'my-1 mt-md-0_25 mb-md-1_5 text-start text-md-center': isRowLt3,
            'mt-0_25 mb-1_5': !isRowLt3,
          })}
        >
          <strong className="category-title d-lg-flex justify-content-center align-items-center mb-0_25">
            {entry.metadata('categoryTitle').value()}
          </strong>
          <span
            className={classnames('nav-title mb-lg-1 text-color-secondary', {
              'text-center': !isRowLt3,
            })}
          >
            {entry.metadata('categoryDescription').value()}
            <span
              className={classnames('d-lg-none icon-more-info text-primary-darker size-12 ms-0_25 me-1 me-lg-1_5', {
                'd-none d-md-inline': isRowLt3,
              })}
              aria-hidden
            />
          </span>
          <button
            rel="nofollow"
            data-tracking-id=""
            className="cta-button mt-auto text-transform-none text-white btn btn-primary-b size-16 d-none d-lg-block align-self-center"
            href="#"
          >
            {entry.metadata('tileCTA').value()}
          </button>
        </div>
        {isRowLt3 && (
          <span className="d-md-none icon-more-info text-primary-darker size-16 ms-0_25 me-1_5" aria-hidden />
        )}
      </a>
    ));

  renderTestimonials = (entries, isCarousel = false) =>
    entries.map(entry => {
      const innerCard = (
        <div
          className={classnames('card  p-1_5 d-flex flex-column', {
            'mx-0_25 mx-lg-1': !isCarousel,
          })}
          key={!isCarousel ? `${entry.metadata('rating').value()} ${entry.metadata('author-name').value()}` : null}
          style={{
            height: '258px',
            minWidth: '275px',
          }}
        >
          {leftQuotesUse()}
          <ContentFragment classes="testimonial size-16" componentToUse="p">
            {entry.metadata('description').value()}
          </ContentFragment>
          <footer className="author-rating-container d-flex align-items-center mt-auto">
            <figure className="me-1 mb-0 d-flex justify-content-center align-items-center">
              <img
                className="avatar"
                src={getStaticImageUrl(entry.metadata('author-avatar').value())}
                alt={`avatar for ${entry.metadata('author-name').value()}`}
                width={68}
                height={68}
              />
            </figure>
            <section className="author-rating size-14">
              <header className="mb-0_25">{entry.metadata('author-name').value()}</header>
              <article className="mb-0">
                <RatingStars
                  rating={parseFloat(entry.metadata('rating').value())}
                  className="text-color-primary me-0_5"
                />
                {entry.metadata('rating').value()}
              </article>
            </section>
          </footer>
        </div>
      );
      const carouselCard = (
        <article className="px-1" key={entry.metadata('rating').value()}>
          {innerCard}
        </article>
      );
      return isCarousel ? carouselCard : innerCard;
    });

  renderWhatWeDo = entries =>
    entries.map(entry => (
      <Fragment key={entry.metadata('dt').value()}>
        <dt className="size-48 fw-300 text-primary mt-2 mt-md-1_5">{entry.metadata('dt').value()}</dt>
        <ContentFragment componentToUse="dd" classes="size-16 mt-0_25 mx-auto p-0_25 text-color-secondary">
          {entry.metadata('dd').value()}
        </ContentFragment>
      </Fragment>
    ));

  renderFaq = entries =>
    entries.map(entry => (
      <details className="text-start py-0_75 bd-c-primary" key={entry.metadata('question').value()}>
        <summary>
          <span className="icon-more-info text-primary-darker size-12 me-1" aria-hidden />
          {entry.metadata('question').value()}
        </summary>
        <em className="px-1_75 pt-1 m-0 d-inline-block">{entry.metadata('answer').value()}</em>
      </details>
    ));

  renderCarShoppingHighlights = entries =>
    entries.map((entry, index) => (
      <Fragment key={entry.metadata('heading').value()}>
        <header
          className={classnames('size-20 text-color-primary text-md-start mt-2 mb-0_5 fw-bold mx-auto', {
            'mt-md-0': index === 0,
            'mt-md-1 mt-lg-2': index > 0,
          })}
        >
          {entry.metadata('heading').value()}
        </header>
        <ContentFragment componentToUse="p" classes="size-16 text-color-secondary text-md-start mx-auto">
          {entry.metadata('description').value()}
        </ContentFragment>
      </Fragment>
    ));

  renderGetStartedTiles = (entries, creativeId, isRowLt3) =>
    entries.map(entry => (
      <a
        href={entry.metadata('tileURL').value()}
        key={`entry-${entry.metadata('categoryTitle').value()}`}
        className={classnames('pos-r align-items-center px-0_5 text-color-primary bg-c-primary size-16 d-flex', {
          'mb-1 mb-lg-0': !isRowLt3,
          'mb-1 mb-md-1_5': isRowLt3,
        })}
        onClick={this.handleClickThruTracker.bind(
          this,
          creativeId,
          entry.metadata('categoryTitle').value().toLowerCase()
        )}
      >
        <figure className="category-icon ms-1 ms-lg-1_5 mb-0 d-flex justify-content-center align-items-center">
          <img
            src={getStaticImageUrl(entry.metadata('categoryIcon').value())}
            alt={`${entry.metadata('categoryTitle').value()} logo`}
            width={20}
            height={23}
          />
        </figure>
        <div className="title-info ms-1 ms-lg-1_5">
          <strong className="category-title d-block">{entry.metadata('categoryTitle').value()}</strong>
          <span className="nav-title mt-0_25 mb-lg-1 text-center text-color-secondary">
            {entry.metadata('categoryDescription').value()}
          </span>
        </div>
        <span className="icon-more-info text-primary-darker size-16 ms-0_25 me-1_5" aria-hidden />
      </a>
    ));

  render() {
    const { content, isMobile, isSmall, breakpoint } = this.props;
    const { isVideoModalOpen, videoModalData } = this.state;

    if (!content) {
      return null;
    }

    const { title, subtitle } = content;
    const navCategories = content.child('nav-categories').children();
    const testimonials = content.child('testimonials');
    const whatWeDo = content.child('what-we-do');
    const carShopping = content.child('car-shopping');
    const faq = content.child('faq');
    const video = content.child('video');
    const getStarted = content.child('lets-get-started');
    const carouselSettings = {
      slidesToShow: 3,
      centerMode: false,
      infinite: false,
      arrows: true,
      swipe: true,
    };
    const heroImage = content.child('hero-image');
    const heroImageSrc = getHeroImage(heroImage, isMobile);
    const useCarousel = !isMobile && !isSmall && breakpoint !== 'md';
    const isRowLt3 = navCategories.length <= 3;
    const hasVideo = video && video.metadata('youtube-videoid').value() && video.metadata('thumbnailURL').value();

    return (
      <main id="main-content" tabIndex={-1} role="main" className="facebook-landing-page">
        <header
          className={classnames('hero-content pos-r text-center', {
            'no-video': !hasVideo,
          })}
          data-tracking-parent={creativeIds.heroHeader}
          style={{
            height: '400px',
          }}
        >
          {heroBg(heroImageSrc)}
          {this.renderHeroHighlights(title, subtitle, video, hasVideo)}
        </header>
        <nav
          aria-label="Main Navigation"
          className={classnames('nav-category-tiles pos-r  pb-1 text-center mx-auto', {
            'row-lt-3': isRowLt3,
          })}
          data-tracking-parent={creativeIds.topNav}
          style={{
            maxWidth: '1400px',
            marginTop: '-126px',
          }}
        >
          {this.renderCategoryTopNav(navCategories, creativeIds.topNav, isRowLt3)}
        </nav>
        <section
          className="testimonials py-2_5 py-lg-3_5 mx-0_5 mx-lg-4_75 text-color-secondary"
          data-tracking-parent={creativeIds.testimonials}
        >
          <h3 className="size-30 mb-0_5 text-color-primary text-center">{testimonials.metadata('heading').value()}</h3>
          <h4 className="size-16 mb-1_5 text-center">{testimonials.metadata('subHeading').value()}</h4>
          {leftQuotesSymbol()}
          {!useCarousel && (
            <article className="slider-wrapper py-1">
              <div className="slider d-flex">{this.renderTestimonials(testimonials.children(), false)}</div>
            </article>
          )}
          {useCarousel && (
            <Carousel {...carouselSettings} className="d-flex carousel-container">
              {this.renderTestimonials(testimonials.children(), true)}
            </Carousel>
          )}
        </section>
        <section
          className="what-we-do text-center py-2_5 py-md-3_5 text-color-primary bg-c-secondary"
          data-tracking-parent={creativeIds.whatWeDo}
        >
          <h3 className="size-30">{whatWeDo.metadata('heading').value()}</h3>
          <dl
            className="size-30 mx-auto"
            style={{
              maxWidth: '753px',
              width: '100vw',
            }}
          >
            {this.renderWhatWeDo(whatWeDo.children())}
          </dl>
        </section>
        <section
          className="faq text-center py-2_5 py-lg-3_5 mx-0_5 text-color-secondary"
          data-tracking-parent={creativeIds.faq}
        >
          <h3 className="size-30 mb-0_5 text-color-primary">{faq.metadata('heading').value()}</h3>
          <h4 className="size-16 mb-1_5">{faq.metadata('subHeading').value()}</h4>
          <article className="mx-auto">{this.renderFaq(faq.children())}</article>
        </section>
        <section
          className="car-shopping text-center py-2_5 py-md-3_5 px-1 px-lg-2 text-color-primary bg-c-secondary"
          data-tracking-parent={creativeIds.carShopping}
        >
          <h3 className="size-30">{carShopping.metadata('heading').value()}</h3>
          <h4 className="size-16 mb-2 mb-md-2_5 mx-auto text-color-secondary">
            {carShopping.metadata('subHeading').value()}
          </h4>
          <Row tag="article">
            <Col xs={12} lg={6} md={7} tag="figure" className="p-0">
              <RenderWhenViewable verticalOffset="100%">
                <img
                  className="w-100 object-fit-contain"
                  src={getStaticImageUrl(carShopping.metadata('imageUrl').value())}
                  alt={carShopping.metadata('imageAlt').value()}
                  width={'100%'}
                  height={'100%'}
                />
              </RenderWhenViewable>
            </Col>
            <Col
              xs={12}
              lg={6}
              md={5}
              tag="section"
              className="d-md-flex flex-column justify-content-center highlights"
            >
              {this.renderCarShoppingHighlights(carShopping.children())}
            </Col>
          </Row>
        </section>
        <section
          className={classnames('get-started py-2_5 py-md-3_5 text-color-secondary', {
            'row-lt-3': isRowLt3,
          })}
          data-tracking-parent={creativeIds.getStarted}
        >
          <h3 className="size-30 mb-1_75 mb-lg-2 text-color-primary text-center">
            {getStarted.metadata('heading').value()}
          </h3>
          <article className="px-0_5 px-lg-2">
            {this.renderGetStartedTiles(navCategories, creativeIds.getStarted, isRowLt3)}
          </article>
        </section>
        <YoutubeVideoModalAsync
          onToggleModal={this.onToggleVideoModal}
          isOpen={isVideoModalOpen}
          videoId={videoModalData.videoId}
          videoAspectRatio={videoModalData.videoAspectRatio}
          creativeId={videoModalData.creativeId}
          videoTitle={videoModalData.videoTitle}
        />
      </main>
    );
  }
}

export const mapStateToProps = state => ({
  isMobile: state.mobile,
  isSmall: get(state, 'pageContext.isSmallDeprecated'),
  breakpoint: get(state, 'pageContext.breakpointDeprecated'),
});

const stateToPropsConfig = {
  content: bindToContent(FACEBOOK_LANDING_PATH),
};

export const FacebookLanding = connect(mapStateToProps)(connectToModel(FacebookLandingUI, stateToPropsConfig));
